import React from 'react';
import { motion } from 'framer-motion';
import sonoraLogo from '../../assets/images/sonora_logo.webp';
import sonoraInApp from '../../assets/images/sonora_in_app.png';

const SonoraAIPage = () => {
    return (
        <div className="min-h-screen bg-dark pt-24 pb-16">
            {/* Background orbs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-accent-purple opacity-15 rounded-full blur-3xl" />
                <div className="absolute top-40 right-20 w-96 h-96 bg-accent-pink opacity-10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-accent-blue opacity-10 rounded-full blur-3xl" />
            </div>

            <div className="max-w-6xl mx-auto px-6 relative z-10">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="flex flex-col lg:flex-row items-center gap-12 mb-16"
                >
                    {/* Left Content */}
                    <div className="flex-1 text-center lg:text-left">
                        <div className="flex items-center gap-4 justify-center lg:justify-start mb-6">
                            <img src={sonoraLogo} alt="Sumo AI" className="w-16 h-16 rounded-2xl" />
                            <div>
                                <h1 className="text-4xl md:text-5xl font-custom1 text-white">
                                    Sumo AI
                                </h1>
                                <span className="text-accent-purple font-custom2">Music Generation App</span>
                            </div>
                        </div>

                        <p className="text-lg font-custom2 text-gray-400 mb-8 leading-relaxed">
                            Create original music with advanced AI algorithms. Generate melodies, harmonies, and full compositions in any genre you can imagine.
                        </p>

                        {/* Stats */}
                        <div className="flex gap-8 justify-center lg:justify-start mb-8">
                            <div>
                                <span className="text-3xl font-custom1 bg-gradient-to-r from-accent-purple to-accent-pink bg-clip-text text-transparent">110K+</span>
                                <p className="text-sm text-gray-500 font-custom2">Downloads</p>
                            </div>
                            <div>
                                <span className="text-3xl font-custom1 text-white">4.2</span>
                                <p className="text-sm text-gray-500 font-custom2">Rating</p>
                            </div>
                        </div>

                        {/* Store Badges */}
                        <div className="flex gap-4 justify-center lg:justify-start">
                            <a
                                href="https://play.google.com/store/apps/details?id=ai.sonora&hl=en_US"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-dark-card border border-gray-600 px-5 py-3 rounded-xl hover:border-accent-blue transition-colors duration-300"
                            >
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                </svg>
                                <span className="font-custom2 text-sm text-white">Play Store</span>
                            </a>
                        </div>
                    </div>

                    {/* Right - Phone Mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex-1 flex justify-center"
                    >
                        <div className="relative w-64 md:w-72">
                            {/* Glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-b from-accent-purple to-accent-pink opacity-25 blur-3xl scale-110" />

                            {/* Phone Frame */}
                            <div className="relative bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 rounded-[3rem] p-[3px] shadow-2xl">
                                <div className="bg-black rounded-[2.8rem] p-1.5">
                                    <div className="relative bg-black rounded-[2.4rem] overflow-hidden aspect-[9/19.5]">
                                        {/* Dynamic Island */}
                                        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20">
                                            <div className="w-24 h-8 bg-black rounded-full flex items-center justify-center gap-3 shadow-lg">
                                                <div className="w-2.5 h-2.5 rounded-full bg-gray-900 ring-1 ring-gray-800" />
                                                <div className="w-3 h-3 rounded-full bg-gray-900 ring-1 ring-gray-700" />
                                            </div>
                                        </div>
                                        <div className="absolute inset-0">
                                            <img src={sonoraInApp} alt="Sumo AI App" className="w-full h-full object-contain object-top" />
                                        </div>
                                    </div>
                                </div>
                                {/* Side buttons */}
                                <div className="absolute -left-[3px] top-28 w-[4px] h-8 bg-gradient-to-b from-gray-600 to-gray-700 rounded-l-md" />
                                <div className="absolute -left-[3px] top-40 w-[4px] h-14 bg-gradient-to-b from-gray-600 to-gray-700 rounded-l-md" />
                                <div className="absolute -left-[3px] top-56 w-[4px] h-14 bg-gradient-to-b from-gray-600 to-gray-700 rounded-l-md" />
                                <div className="absolute -right-[3px] top-36 w-[4px] h-20 bg-gradient-to-b from-gray-600 to-gray-700 rounded-r-md" />
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Features */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mb-16"
                >
                    <h2 className="text-2xl font-custom1 text-white mb-8 text-center">Features</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: "🎵", title: "AI Music Generation", desc: "Create original melodies and harmonies" },
                            { icon: "🎸", title: "Multiple Genres", desc: "From classical to electronic" },
                            { icon: "⚡", title: "Real-time Creation", desc: "Generate music instantly" },
                            { icon: "📤", title: "Export & Share", desc: "Share your creations easily" },
                        ].map((feature, idx) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                                className="bg-dark-card border border-gray-600 rounded-2xl p-6 hover:border-accent-purple transition-colors duration-300"
                            >
                                <span className="text-3xl mb-4 block">{feature.icon}</span>
                                <h3 className="text-lg font-custom1 text-white mb-2">{feature.title}</h3>
                                <p className="text-sm font-custom2 text-gray-400">{feature.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* About */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="bg-dark-card border border-gray-600 rounded-2xl p-8 mb-16"
                >
                    <h2 className="text-2xl font-custom1 text-white mb-6">About Sumo AI</h2>
                    <div className="space-y-4 text-gray-400 font-custom2 leading-relaxed">
                        <p>
                            Sumo AI is a cutting-edge music generation application that allows you to create high-quality music using advanced AI algorithms. Whether you're an experienced musician, a songwriter, or someone who simply enjoys exploring new sounds, Sumo AI offers an intuitive platform where creativity meets technology.
                        </p>
                        <p>
                            With its user-friendly interface, Sumo AI makes it easy for anyone to dive into music production without the need for expensive equipment or extensive technical knowledge. The AI-powered engine is designed to understand the nuances of different music genres, allowing users to generate compositions ranging from classical to electronic, jazz, rock, and beyond.
                        </p>
                        <p>
                            One of the most exciting features of Sumo AI is its ability to generate original melodies, harmonies, and rhythms in real-time. You can customize the key, tempo, and instruments used in each composition, making it easy to tailor the music to your exact preferences.
                        </p>
                    </div>
                </motion.div>

                {/* Policies */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="text-center mb-10"
                >
                    <p className="text-gray-500 font-custom2 text-sm">
                        By using Sumo AI, you agree to our{' '}
                        <a href="/sonora-ai/privacy-policy" className="text-accent-purple hover:underline">Privacy Policy</a>,{' '}
                        <a href="/sonora-ai/terms-and-conditions" className="text-accent-purple hover:underline">Terms and Conditions</a>, and{' '}
                        <a href="/sonora-ai/eula" className="text-accent-purple hover:underline">EULA</a>.
                    </p>
                </motion.div>

                {/* Footer */}
                <div className="text-center">
                    <p className="text-gray-600 font-custom2 text-sm">© 2024 Sumo AI. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default SonoraAIPage;
